import { GridFields, InfoCard } from 'components';
import { lodash } from 'helpers';
import { useTranslation } from 'libs/i18n';
import { Chip, MenuOption } from 'libs/ui';
import { Icon } from 'libs/ui/Icon';
import { Colors } from 'libs/ui/theme';
import { CountryRiskBadges } from 'modules/client/common/feature';

import { BeneficiaryItem, BeneficiaryOptionType } from './types';

export interface BeneficiariesInfoCardProps {
  items: BeneficiaryItem[];
  editable?: boolean;
  onClickDelete?: (row: BeneficiaryItem) => void;
  onCellClick: (row: BeneficiaryItem) => void;
  onClickArchive?: (row: BeneficiaryItem) => void;
}

export const BeneficiariesInfoCard = ({
  items,
  onCellClick,
  onClickArchive,
  onClickDelete,
  editable,
}: BeneficiariesInfoCardProps) => {
  const { t } = useTranslation();

  const shownOptions: Partial<Record<BeneficiaryOptionType, boolean>> = {
    archive: !!onClickArchive,
    dearchive: !!onClickArchive,
    delete: !!onClickDelete,
  };

  const onClickMenuOption = (
    item: BeneficiaryItem,
    option: MenuOption<BeneficiaryOptionType>
  ) => {
    switch (option.value) {
      case BeneficiaryOptionType.delete:
        if (item.id) {
          onClickDelete?.(item);
        }
        break;
      case BeneficiaryOptionType.archive:
      case BeneficiaryOptionType.dearchive:
        if (item.id) {
          onClickArchive?.(item);
        }
        break;
      default:
        break;
    }
  };

  return (
    <GridFields>
      {items.map((item) => (
        <InfoCard
          fields={[
            {
              label: t('client.legal.companyBeneficiaries.columns.email'),
              value: item.email,
            },
            {
              label: t('client.legal.companyBeneficiaries.columns.status'),
              value: (
                <Chip
                  size="xs"
                  text={
                    item.archived
                      ? t('common.archivedStatus')
                      : t('common.activeStatus')
                  }
                  variant={item.archived ? 'gray' : 'green'}
                />
              ),
            },
            {
              label: t('client.legal.companyBeneficiaries.columns.country'),
              value: item.country,
              valueAdornment: item.countryRisk && (
                <CountryRiskBadges {...item.countryRisk} />
              ),
            },
            {
              label: t(
                'client.legal.companyBeneficiaries.columns.capitalParticipationPercentage'
              ),
              value: item.capitalParticipationPercentage,
            },
          ]}
          menuOptions={
            editable
              ? lodash.compact([
                  shownOptions.archive &&
                    !item.archived && {
                      value: BeneficiaryOptionType.archive,
                      label: t('common.archive'),
                      Icon: Icon.TimeSleep,
                    },
                  shownOptions.dearchive &&
                    item.archived && {
                      value: BeneficiaryOptionType.dearchive,
                      label: t('common.dearchive'),
                      Icon: Icon.RotateRightCheck,
                    },
                  shownOptions.delete && {
                    value: BeneficiaryOptionType.delete,
                    label: t('common.delete'),
                    Icon: Icon.Bucket,
                    color: Colors.red,
                  },
                ])
              : undefined
          }
          title={item.name}
          variant={item.archived ? 'gray' : 'default'}
          onClick={() => onCellClick(item)}
          onClickMenuOption={(option) => onClickMenuOption(item, option)}
        />
      ))}
    </GridFields>
  );
};
